import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { forgotPassword, resetPassword } from '../helpers/user';
import usePageTitle from '../hooks/usePageTitle';
import '../css/ForgotPassword.css';

const PasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Get code from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code');
  
  usePageTitle(code ? 'Réinitialiser le mot de passe' : 'Mot de passe oublié');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      await forgotPassword(email);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      if (password !== passwordConfirmation) {
        throw new Error('Les mots de passe ne correspondent pas.');
      }

      await resetPassword(code, password, passwordConfirmation);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message);
    }
  };

  // Validate code on mount for reset password page
  useEffect(() => {
    if (code && (!code.length || code.length < 8)) {
      navigate('/mot-de-passe-oublie');
    }
  }, [code, navigate]);

  if (code) {
    return (
      <div className="forgot-password-page">
        <div className="auth-container">
          <Link to="/connexion-inscription" className="back-link">
            <ArrowLeft size={20} />
            <span>Retour</span>
          </Link>

          <div className="auth-box">
            <div className="auth-header">
              <h1>Réinitialiser le mot de passe</h1>
              <p>Entrez votre nouveau mot de passe ci-dessous.</p>
            </div>

            {status === 'success' ? (
              <div className="success-message">
                <CheckCircle size={48} />
                <h2>Mot de passe modifié!</h2>
                <p>
                  Votre mot de passe a été réinitialisé avec succès.
                  Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                </p>
                <Link to="/connexion-inscription" className="btn-primary">
                  Aller à la connexion
                </Link>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="auth-form">
                <div className="form-group">
                  <label htmlFor="password">Nouveau mot de passe</label>
                  <div className="input-wrapper">
                    <Lock size={20} />
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Votre nouveau mot de passe"
                      required
                      disabled={status === 'loading'}
                      minLength={8}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="passwordConfirmation">Confirmer le mot de passe</label>
                  <div className="input-wrapper">
                    <Lock size={20} />
                    <input
                      type="password"
                      id="passwordConfirmation"
                      value={passwordConfirmation}
                      onChange={(e) => setPasswordConfirmation(e.target.value)}
                      placeholder="Confirmez votre mot de passe"
                      required
                      disabled={status === 'loading'}
                      minLength={8}
                    />
                  </div>
                </div>

                {status === 'error' && (
                  <div className="error-message">
                    <AlertCircle size={20} />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <button 
                  type="submit" 
                  className={`btn-primary ${status === 'loading' ? 'loading' : ''}`}
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? 'Modification en cours...' : 'Modifier le mot de passe'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <div className="auth-container">
        <Link to="/connexion-inscription" className="back-link">
          <ArrowLeft size={20} />
          <span>Retour</span>
        </Link>

        <div className="auth-box">
          <div className="auth-header">
            <h1>Mot de passe oublié?</h1>
            <p>Entrez votre adresse e-mail ci-dessous pour réinitialiser votre mot de passe.</p>
          </div>

          {status === 'success' ? (
            <div className="success-message">
              <CheckCircle size={48} />
              <h2>Email envoyé!</h2>
              <p>
                Si un compte existe avec l'adresse {email}, vous recevrez un email avec les instructions 
                pour réinitialiser votre mot de passe.
              </p>
              <p className="note">
                N'oubliez pas de vérifier vos spams si vous ne trouvez pas l'email.
              </p>
              <Link to="/connexion-inscription" className="btn-primary">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Adresse email</label>
                <div className="input-wrapper">
                  <Mail size={20} />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                    disabled={status === 'loading'}
                  />
                </div>
              </div>

              {status === 'error' && (
                <div className="error-message">
                  <AlertCircle size={20} />
                  <span>{errorMessage}</span>
                </div>
              )}

              <button 
                type="submit" 
                className={`btn-primary ${status === 'loading' ? 'loading' : ''}`}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Envoi en cours...' : 'Réinitialiser le mot de passe'}
              </button>

              <div className="auth-links">
                <span>Vous vous en souvenez?</span>
                <Link to="/connexion-inscription">Connexion</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordPage;